import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ShieldCheck, Baby, BadgeCheck, ArrowRight, Truck, Magnet } from "lucide-react"
import heroImage from "@/assets/hero-image-new.webp"
import heroImageMd from "@/assets/hero-image-new-md.webp"
import heroImageSm from "@/assets/hero-image-new-sm.webp"

const FloatingShape = ({
  color,
  size,
  position,
  delay,
}: {
  color: string
  size: number
  position: { top?: string; right?: string; bottom?: string; left?: string }
  delay: number
}) => (
  <div
    className="absolute shape-shadow animate-hero-float"
    style={{
      width: size,
      height: size,
      ...position,
      animationDelay: `${delay}s`,
    }}
  >
    <div className={`h-full w-full rounded-xl rotate-12 ${color}`} />
  </div>
)

const Hero = () => {
  return (
    <section
      className="relative gradient-hero overflow-hidden"
      style={{ paddingTop: "calc(var(--announcement-height, 36px) + 64px)" }}
    >
      {/* Floating shapes - hidden on mobile */}
      <div className="absolute inset-0 pointer-events-none hidden md:block">
        <FloatingShape color="bg-gold/30" size={72} position={{ top: "10%", right: "3%" }} delay={0} />
        <FloatingShape color="bg-primary/25" size={52} position={{ top: "60%", right: "2%" }} delay={0.3} />
        <FloatingShape color="bg-primary/20" size={32} position={{ bottom: "20%", left: "4%" }} delay={1.2} />
        <FloatingShape color="bg-gold/25" size={40} position={{ top: "15%", left: "6%" }} delay={0.4} />
        <FloatingShape color="bg-blue/15" size={28} position={{ top: "50%", left: "3%" }} delay={0.7} />
        <FloatingShape color="bg-gold/15" size={24} position={{ bottom: "10%", right: "5%" }} delay={1.0} />
      </div>

      <div className="container relative z-10 mx-auto flex min-h-[calc(80vh-var(--announcement-height,36px)-64px)] items-center px-8 py-10 md:px-4 md:py-12">
        <div className="flex w-full flex-col items-center gap-10 md:flex-row md:gap-16 lg:gap-20">
          {/* Left: Content */}
          <div className="max-w-xl text-center md:w-1/2 md:text-left">
            {/* USP chip */}
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 animate-hero-fade-in">
              <Magnet className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} />
              <span className="text-sm font-medium text-primary">
                Kupuješ lahko tudi po kosih
              </span>
            </div>

            <h1
              className="font-display text-4xl font-bold leading-tight text-foreground sm:text-5xl lg:text-6xl animate-hero-fade-up"
              style={{ lineHeight: 1.15, animationDelay: "0.1s" }}
            >
              Zgradi svoj svet.<br />
              <span className="whitespace-nowrap bg-gradient-to-r from-teal to-teal-light bg-clip-text text-transparent">Košček za koščkom.</span>
            </h1>

            <p
              className="mb-8 mt-5 max-w-md text-lg leading-relaxed text-muted-foreground mx-auto md:mx-0 animate-hero-fade-up"
              style={{ animationDelay: "0.2s" }}
            >
              Od prve ceste do celega mesta. Zgrajeno z domišljijo.
            </p>

            <div
              className="mb-8 flex justify-center md:justify-start animate-hero-fade-up"
              style={{ animationDelay: "0.3s" }}
            >
              <Button size="lg" asChild className="group h-12 font-semibold text-base px-8 shadow-button">
                <Link to="/trgovina">
                  V trgovino
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>

            {/* Trust row */}
            <div
              className="flex flex-nowrap items-center justify-center gap-3 sm:gap-5 md:justify-start animate-hero-fade-up"
              style={{ animationDelay: "0.4s" }}
            >
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
                <Baby className="h-4 w-4 text-primary" />
                <span>Starost 3+</span>
              </div>
              <div className="hidden h-4 w-px bg-border sm:block" />
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span>CE certifikat</span>
              </div>
              <div className="hidden h-4 w-px bg-border sm:block" />
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
                <BadgeCheck className="h-4 w-4 text-primary" />
                <span>EN71 testirano</span>
              </div>
            </div>
          </div>

          {/* Right: Image */}
          <div className="relative md:w-1/2 max-w-md mx-auto md:max-w-lg">
            {/* Gradient glow behind image */}
            <div className="absolute inset-0 scale-110 rounded-full bg-gradient-to-br from-gold/20 via-primary/15 to-gold/10 blur-3xl" />

            {/* Decorative layered shapes */}
            <div className="absolute -right-4 -top-4 bottom-4 left-4 rounded-3xl bg-primary/8" />
            <div className="absolute -bottom-3 -left-3 right-6 top-6 rounded-3xl bg-gold/8" />

            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <img
                src={heroImage}
                srcSet={`${heroImageSm} 512w, ${heroImageMd} 768w, ${heroImage} 1086w`}
                sizes="(max-width: 768px) 75vw, 40vw"
                alt="Otroci se igrajo z barvitimi magnetnimi ploščicami"
                className="w-full object-cover aspect-[4/5] max-h-[60vh] md:max-h-[70vh]"
                width={1086}
                height={1448}
                fetchPriority="high"
                decoding="async"
              />

              {/* Floating free shipping badge */}
              <div
                className="absolute bottom-4 right-4 flex items-center gap-3 rounded-2xl bg-primary p-3.5 text-primary-foreground shadow-button max-w-[260px] animate-hero-fade-up"
                style={{ animationDelay: "0.4s" }}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20">
                  <Truck className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold leading-tight">Brezplačna dostava</p>
                  <p className="text-xs text-primary-foreground/80">Pri naročilih nad 45 €</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero
